import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  RadialLinearScale,
  Title,
  Tooltip,
} from "chart.js";
import { format } from "date-fns";
import { motion } from "framer-motion";
import { Activity, Award, BarChart2, Clock, Music, Timer, TrendingUp, Users, Zap } from "lucide-react";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import { useStatsStore } from "../stores/statsStore";
import { formatTime } from "../utils/formatTime";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler
);

const StatCard = ({ icon: Icon, title, value, color }: any) => (
  <motion.div whileHover={{ scale: 1.02 }} className="bg-zinc-800 p-6 rounded-lg">
    <div className="flex items-center gap-3 mb-4">
      <div className={`p-3 rounded-full ${color}`}>
        <Icon className="w-6 h-6" />
      </div>
      <h3 className="text-lg font-semibold">{title}</h3>
    </div>
    <p className="text-3xl font-bold">{value}</p>
  </motion.div>
);

export const Statistics = () => {
  const {
    totalPlayTime,
    getPlayTimeByPeriod,
    getMostPlayedTracks,
    getActiveHours,
    getListenerType,
    getListeningTrendsByDay,
    getListeningTrendsByMonth,
    getSourceDistribution,
    getUniqueTracksPlayed,
    getUniqueArtistsPlayed,
    getAverageTrackDuration,
    getSkipRate,
    getMostActiveDay,
    getCurrentStreak,
    getLongestStreak,
    getAverageSessionDuration,
  } = useStatsStore();

  const activeHours = getActiveHours();
  const hourLabels = Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, "0")}:00`);

  const activityData = {
    labels: hourLabels,
    datasets: [
      {
        label: "Listening Activity",
        data: hourLabels.map((hour) => activeHours[parseInt(hour)] || 0),
        borderColor: "rgb(74, 222, 128)",
        backgroundColor: "rgba(74, 222, 128, 0.5)",
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const weeklyTrends = getListeningTrendsByDay();
  const weekdayData = {
    labels: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
    datasets: [
      {
        label: "Weekly Listening Pattern",
        data: weeklyTrends,
        backgroundColor: "rgba(74, 222, 128, 0.8)",
      },
    ],
  };

  const sourceDistribution = getSourceDistribution();
  const sourceData = {
    labels: ["Spotify", "Local Files"],
    datasets: [
      {
        data: [sourceDistribution.spotify, sourceDistribution.local],
        backgroundColor: ["#1DB954", "#4ade80"],
      },
    ],
  };

  const mostPlayed = getMostPlayedTracks(5);
  const topTracksData = {
    labels: mostPlayed.map(({ track }) => track.title),
    datasets: [
      {
        label: "Play Count",
        data: mostPlayed.map((track) => track.count),
        backgroundColor: ["#4ade80", "#22c55e", "#16a34a", "#15803d", "#166534"],
      },
    ],
  };

  const mostActiveDay = getMostActiveDay();

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-center gap-3 mb-8">
        <Activity className="w-8 h-8" />
        <h1 className="text-4xl font-bold mb-2">Statistics</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          icon={Clock}
          title="Total Play Time"
          value={formatTime(totalPlayTime)}
          color="bg-yellow-500/20 text-yellow-500"
        />
        <StatCard
          icon={Music}
          title="Unique Tracks"
          value={getUniqueTracksPlayed()}
          color="bg-blue-500/20 text-blue-500"
        />
        <StatCard
          icon={Users}
          title="Artists"
          value={getUniqueArtistsPlayed()}
          color="bg-purple-500/20 text-purple-500"
        />
        <StatCard
          icon={Timer}
          title="Avg. Duration"
          value={formatTime(getAverageTrackDuration())}
          color="bg-orange-500/20 text-orange-500"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          icon={Zap}
          title="Current Streak"
          value={`${getCurrentStreak()} days`}
          color="bg-yellow-500/20 text-yellow-500"
        />
        <StatCard
          icon={Award}
          title="Longest Streak"
          value={`${getLongestStreak()} days`}
          color="bg-pink-500/20 text-pink-500"
        />
        <StatCard
          icon={BarChart2}
          title="Skip Rate"
          value={`${(getSkipRate() * 100).toFixed(1)}%`}
          color="bg-red-500/20 text-red-500"
        />
        <StatCard
          icon={TrendingUp}
          title="Avg. Session"
          value={formatTime(getAverageSessionDuration() / 1000)}
          color="bg-indigo-500/20 text-indigo-500"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <div className="bg-zinc-800 p-6 rounded-lg">
          <h2 className="text-xl font-semibold mb-6">Daily Activity</h2>
          <Line
            data={activityData}
            options={{
              responsive: true,
              scales: {
                y: {
                  beginAtZero: true,
                  grid: {
                    color: "rgba(255, 255, 255, 0.1)",
                  },
                },
                x: {
                  grid: {
                    display: false,
                  },
                },
              },
              plugins: {
                legend: {
                  display: false,
                },
              },
            }}
          />
        </div>

        <div className="bg-zinc-800 p-6 rounded-lg">
          <h2 className="text-xl font-semibold mb-6">Weekly Pattern</h2>
          <Bar
            data={weekdayData}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  display: false,
                },
              },
              scales: {
                y: {
                  beginAtZero: true,
                  grid: {
                    color: "rgba(255, 255, 255, 0.1)",
                  },
                },
              },
            }}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-zinc-800 p-6 rounded-lg">
          <h2 className="text-xl font-semibold mb-6">Top Tracks</h2>
          <Bar
            data={topTracksData}
            options={{
              indexAxis: "y" as const,
              responsive: true,
              plugins: {
                legend: {
                  display: false,
                },
              },
              scales: {
                x: {
                  beginAtZero: true,
                  grid: {
                    color: "rgba(255, 255, 255, 0.1)",
                  },
                },
              },
            }}
          />
        </div>

        <div className="bg-zinc-800 p-6 rounded-lg">
          <h2 className="text-xl font-semibold mb-6">Source Distribution</h2>
          <div className="aspect-square max-w-md mx-auto">
            <Doughnut
              data={sourceData}
              options={{
                responsive: true,
                plugins: {
                  legend: {
                    position: "bottom",
                  },
                },
              }}
            />
          </div>
        </div>
      </div>

      <div className="mt-8 bg-zinc-800 p-6 rounded-lg">
        <h2 className="text-xl font-semibold mb-4">Additional Insights</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <h3 className="text-lg font-medium mb-2">Most Active Day</h3>
            <p className="text-zinc-400">
              {format(mostActiveDay.date, "MMMM do, yyyy")}
              <br />
              {mostActiveDay.playCount} tracks played
            </p>
          </div>
          <div>
            <h3 className="text-lg font-medium mb-2">Today's Progress</h3>
            <p className="text-zinc-400">
              {formatTime(getPlayTimeByPeriod("day"))} listened
              <br />
              {getAverageSessionDuration() > 0
                ? Math.round((getPlayTimeByPeriod("day") / getAverageSessionDuration()) * 100)
                : 0}
              % of daily average
            </p>
          </div>
          <div>
            <h3 className="text-lg font-medium mb-2">Listening Profile</h3>
            {(() => {
              const listenerType = getListenerType();
              return (
                <div className="space-y-2">
                  <p className="text-yellow-500 font-semibold">{listenerType.type}</p>
                  <p className="text-zinc-400 text-sm">{listenerType.description}</p>
                  <ul className="text-zinc-400 text-sm space-y-1">
                    {listenerType.traits.map((trait, index) => (
                      <li key={index} className="flex items-center gap-2">
                        <span className="w-1 h-1 bg-yellow-500 rounded-full" />
                        {trait}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};
